import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Btn, ErrorBanner, Spinner } from "./_shared";
import { Copy, Check, Download, ExternalLink, X as XIcon, Rocket } from "lucide-react";

const PLATFORMS = [
  { id: "whatsapp", label: "WhatsApp", color: "#25D366" },
  { id: "twitter",  label: "X / Twitter", color: "#0A0A0A" },
  { id: "facebook", label: "Facebook", color: "#1877F2" },
  { id: "instagram", label: "Instagram", color: "#E4405F" },
];

/**
 * Launch Kit modal — auto-rendered when a poll is created and re-openable
 * from each poll row. Shows OG card preview + downloadable poster + 4 ready
 * captions. Zero LLM cost: every caption is a deterministic template.
 *
 * Usage:
 *   <LaunchKitModal slug="kerala-stray-dogs-public-safety" prefetched={data} onClose={...} />
 *   prefetched is optional — if missing, fetches /admin/polls/{slug}/launch-kit
 */
export const LaunchKitModal = ({ slug, prefetched = null, onClose }) => {
  const [kit, setKit] = useState(prefetched);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (kit || !slug) return;
    adminApi.get(`/admin/polls/${slug}/launch-kit`)
      .then((r) => setKit(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load launch kit."));
  }, [slug, kit]);

  const handleCopy = async (key, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1500); }
    catch { /* ignore */ }
  };

  const handleDownload = async (url, filename) => {
    try {
      const r = await fetch(url, { credentials: "omit" });
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch { /* ignore */ }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center items-start py-10 overflow-y-auto px-4"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
      data-testid="launch-kit-modal"
    >
      <div
        className="w-full max-w-3xl border-2 border-[#1f1f1f]"
        style={{ background: "#0F0F0F" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b-2 border-[#1f1f1f]" style={{ background: "#0F0F0F" }}>
          <div className="flex items-center gap-3">
            <Rocket size={20} strokeWidth={3} style={{ color: "#FF9933" }} />
            <div>
              <p className="text-[10px] uppercase font-extrabold tracking-[0.4em]" style={{ color: "#FF9933" }}>Launch Kit</p>
              <h2 className="font-display uppercase text-xl text-[#FDFBF7] mt-1">Share your poll in one click</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:opacity-80" data-testid="launch-kit-close">
            <XIcon size={20} strokeWidth={3} color="#FDFBF7" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {err && <ErrorBanner message={err} />}
          {!kit && !err && <Spinner label="Building your launch kit…" />}

          {kit && (
            <>
              {/* Headline */}
              <div className="border-2 border-[#1f1f1f] p-4" style={{ background: "#141414" }}>
                <p className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500">{kit.category}</p>
                <h3 className="font-display uppercase text-lg mt-1 text-[#FDFBF7]" data-testid="launch-kit-headline">{kit.headline}</h3>
                <a
                  href={kit.assets.poll_url} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] uppercase font-bold tracking-[0.3em] mt-2 inline-flex items-center gap-1.5"
                  style={{ color: "#FF9933" }}
                  data-testid="launch-kit-poll-url"
                >
                  Visit poll <ExternalLink size={10} strokeWidth={3} />
                </a>
              </div>

              {/* Visual assets */}
              <div className="grid md:grid-cols-2 gap-4">
                <AssetCard
                  label="OG card · 1200×630"
                  hint="Used by WhatsApp / iMessage / Twitter / FB link previews."
                  url={kit.assets.og_image}
                  filename={`kerala-speaks-${kit.slug}-og.png`}
                  testIdPrefix="og"
                  onDownload={handleDownload}
                />
                <AssetCard
                  label="Launch poster · 1080×1350"
                  hint="Ready for Instagram feed / Facebook story / print."
                  url={kit.assets.poster}
                  filename={`kerala-speaks-${kit.slug}-poster.png`}
                  testIdPrefix="poster"
                  onDownload={handleDownload}
                />
              </div>

              {/* Captions */}
              <div className="space-y-3" data-testid="launch-kit-captions">
                {PLATFORMS.map((p) => (
                  <CaptionCard
                    key={p.id}
                    platform={p}
                    text={kit.captions[p.id] || ""}
                    copied={copied === p.id}
                    onCopy={() => handleCopy(p.id, kit.captions[p.id] || "")}
                  />
                ))}
              </div>

              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 pt-2 border-t border-[#1f1f1f]">
                All captions are zero-cost templates · Sharing the share-URL ensures the correct preview card appears.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AssetCard = ({ label, hint, url, filename, testIdPrefix, onDownload }) => (
  <div className="border-2 border-[#1f1f1f] p-3" style={{ background: "#0A0A0A" }} data-testid={`launch-kit-asset-${testIdPrefix}`}>
    <p className="text-[10px] uppercase font-extrabold tracking-[0.3em]" style={{ color: "#FF9933" }}>{label}</p>
    <p className="text-[10px] text-neutral-500 mt-1 leading-relaxed">{hint}</p>
    <div className="mt-3 border border-[#1f1f1f] overflow-hidden" style={{ background: "#FDFBF7" }}>
      <img
        src={url} alt={label}
        loading="lazy"
        className="w-full h-auto block"
        data-testid={`launch-kit-${testIdPrefix}-img`}
      />
    </div>
    <div className="mt-3 flex gap-2">
      <Btn variant="ghost" onClick={() => onDownload(url, filename)} data-testid={`launch-kit-${testIdPrefix}-download`}>
        <Download size={11} strokeWidth={3} /> Download
      </Btn>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <Btn variant="ghost" data-testid={`launch-kit-${testIdPrefix}-open`}>
          <ExternalLink size={11} strokeWidth={3} /> Open
        </Btn>
      </a>
    </div>
  </div>
);

const CaptionCard = ({ platform, text, copied, onCopy }) => (
  <div className="border-2 border-[#1f1f1f] p-4" style={{ background: "#141414" }} data-testid={`launch-kit-caption-${platform.id}`}>
    <div className="flex items-center justify-between mb-2 gap-3">
      <span
        className="text-[10px] uppercase font-extrabold tracking-[0.3em] px-2 py-1 border"
        style={{ borderColor: platform.color, color: platform.color, background: "transparent" }}
      >
        {platform.label}
      </span>
      <button
        type="button"
        onClick={onCopy}
        className="text-[10px] uppercase font-extrabold tracking-[0.25em] flex items-center gap-1.5 hover:opacity-80"
        style={{ color: copied ? "#22c55e" : "#FF9933" }}
        data-testid={`launch-kit-copy-${platform.id}`}
      >
        {copied ? <><Check size={11} strokeWidth={3} /> Copied</> : <><Copy size={11} strokeWidth={3} /> Copy</>}
      </button>
    </div>
    <pre className="text-xs text-neutral-200 whitespace-pre-wrap font-sans leading-relaxed select-text">
      {text}
    </pre>
  </div>
);

export default LaunchKitModal;
